import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { applyAdminAppMeta } from '../../utils/pwa';
import { motion } from 'framer-motion';
import { AlertCircleIcon, Loader2Icon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { company } from '../../data/site';
export function AdminLogin() {
  const {
    loggedIn,
    authLoading,
    login
  } = useAdminData();
  const navigate = useNavigate();
  const location = useLocation();
  // Efter login sendes man videre til den side, man forsøgte at åbne.
  const requested = (location.state as {
    from?: string;
  } | null)?.from;
  const destination = requested && requested.startsWith('/admin') && !requested.startsWith('/admin/login') ? requested : '/admin';
  useEffect(() => applyAdminAppMeta(), []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Mens Firebase undersøger, om der allerede findes en gemt session,
  // vises en indlæsningstilstand — ikke login-formularen.
  if (authLoading) {
    return <div className="flex min-h-screen w-full items-center justify-center bg-canvas px-5" data-id="element-870" data-mp="2an4tgqsh7neh">
        <p className="flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-871" data-mp="1d17e329e7y6r">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-872" data-mp="1hy7o1o9d5l35" />
          Henter …
        </p>
      </div>;
  }
  if (loggedIn) return <Navigate to={destination} replace data-id="element-873" data-mp="1mvzdmbmjcv46" />;
  return <div className="flex min-h-screen w-full items-center bg-canvas px-5 py-12" data-id="element-874" data-mp="1rp28cn31ualy">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1]
    }} className="mx-auto w-full max-w-sm" data-id="element-875" data-mp="u9obuxn70o5s">
        <img src={company.logoWide} alt="LYKKEVÆRK" className="h-14 w-auto" data-id="element-876" data-mp="r70ffmb8wjrp" />
        <h1 className="mt-4 font-display text-xl font-semibold tracking-tight text-ink-soft" data-id="element-877" data-mp="n6gh4fexra3z">Admin</h1>

        <form className="mt-8 rounded-card border border-line bg-white p-6 md:p-8" onSubmit={async event => {
        event.preventDefault();
        if (loading) return;
        setLoading(true);
        const result = await login(email, password);
        if (!result.ok) {
          setError(result.error ?? 'Der opstod en fejl. Prøv igen.');
          setErrorCode(result.code ?? '');
          setPassword('');
          setLoading(false);
          return;
        }
        setError('');
        setErrorCode('');
        navigate(destination, {
          replace: true
        });
      }} data-id="element-878" data-mp="10xbua3wgwddd">
          <TextField id="admin-mail" label="E-mail" type="email" required autoComplete="off" value={email} onChange={event => {
          setEmail(event.target.value);
          if (error) {
            setError('');
            setErrorCode('');
          }
        }} data-id="element-879" data-mp="1tkfvdxsfw7jv" />
          <TextField id="admin-kode" label="Password" type="password" required autoComplete="current-password" className="mt-5" value={password} onChange={event => {
          setPassword(event.target.value);
          if (error) {
            setError('');
            setErrorCode('');
          }
        }} data-id="element-880" data-mp="2xlpa329q7a8h" />

          {error && <div role="alert" className="mt-4 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-881" data-mp="3lha0mmdyok49">
              <p className="flex items-start gap-2" data-id="element-882" data-mp="d06tu4j0r4r4">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-883" data-mp="ht8b2j6p7gdv" />
                {error}
              </p>
              {errorCode && <p className="mt-1.5 pl-6 font-mono text-xs text-danger/80" data-id="element-884" data-mp="2kdgjks5qopnd">{errorCode}</p>}
            </div>}

          <Button type="submit" size="lg" className="mt-7 w-full" disabled={loading || authLoading} data-id="element-885" data-mp="2gb3gycmah7ut">
            {loading ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-886" data-mp="2bev6vdmy84ui" />
                Logger ind …
              </> : 'Log ind'}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-ink-muted" data-id="element-887" data-mp="2zar4st86p9wa">
          <Link to="/" className="transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-888" data-mp="2z965yreu74sm">
            Tilbage til hjemmesiden
          </Link>
        </p>
      </motion.div>
    </div>;
}